// COP — Captcha verification client helper.
//
// Thin wrapper around the `verifyCaptcha` Supabase Edge Function. Callers
// MUST invoke `verifyCaptcha(token, action)` before calling
// `supabase.auth.signUp` / `supabase.auth.resetPasswordForEmail` (or the
// equivalent login flow) and abort the auth call if the server returns
// `success: false`.
//
// We intentionally return a structured result instead of throwing — the calling
// component decides what to do (show toast, reset captcha widget, etc.).

import { supabase } from '@/integrations/supabase/client';

export interface VerifyCaptchaResult {
  success: boolean;
  codes?: string[];
  /** True when the network call itself failed (vs. captcha returning success=false). */
  networkError?: boolean;
}

/**
 * Verify an hCaptcha token via the `verifyCaptcha` edge function.
 *
 * @param token   The token returned by the hCaptcha widget's `onVerify`.
 * @param action  Optional label for logging (`'signup'`, `'login'`, `'forgot_password'`).
 */
export async function verifyCaptcha(
  token: string,
  action?: string
): Promise<VerifyCaptchaResult> {
  if (!token) {
    return { success: false, codes: ['missing-input-response'] };
  }

  try {
    const { data, error } = await supabase.functions.invoke('verifyCaptcha', {
      body: { token, action },
    });

    if (error) {
      console.error('verifyCaptcha invoke error', error);
      return { success: false, networkError: true };
    }

    const payload = data as VerifyCaptchaResult | null;
    if (!payload || typeof payload.success !== 'boolean') {
      console.error('verifyCaptcha: unexpected response shape', data);
      return { success: false, networkError: true };
    }

    return payload;
  } catch (err) {
    console.error('verifyCaptcha threw', err);
    return { success: false, networkError: true };
  }
}
